import { formatDate } from '@angular/common';
import { Component, Injectable, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbTimeAdapter, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { combineLatest, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  tap,
} from 'rxjs/operators';

import { DatetimeService } from '~app/services/datetime.service';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/**
 * Model: `/\d\d:\d\d:00/`
 * @export
 * @class CiaoDateTimeStringAdapter
 * @extends {NgbTimeAdapter<string>}
 */
@Injectable()
export class CiaoDateTimeStringAdapter extends NgbTimeAdapter<string> {
  /**
   *
   * @param value Part of an iso string, `hh:mm:ss`, can be `hh:mm`.  This is in zulu time.
   * @returns {NgbTimeStruct} in local time
   */
  fromModel(value: string | null): NgbTimeStruct | null {
    if (!value) {
      return null;
    }
    let matcher = /(\d\d):(\d\d)/;
    let groups = value.match(matcher);
    if (!groups) {
      throw new Error(
        'Cannot find hh:mm in the provided string ' + JSON.stringify(value)
      );
    }
    return {
      hour: Number.parseInt(groups[1], 10),
      minute: Number.parseInt(groups[2], 10),
      second: 0,
    };
  }

  toModel(time: NgbTimeStruct | null): string | null {
    if (time == null) {
      return null;
    }
    let tempDate = new Date(`1970-01-01T00:00`);
    tempDate.setHours(time.hour);
    tempDate.setMinutes(time.minute);
    return formatDate(tempDate, 'HH:mm:ss', 'en-us');
  }
}

@Component({
  selector: 'ciao-form-field-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.less'],
  providers: [{ provide: NgbTimeAdapter, useClass: CiaoDateTimeStringAdapter }],
  standalone: false,
})
export class DateTimeInputComponent implements OnInit, OnDestroy {
  @Input() formControlInput: UntypedFormControl;
  @Input('label') label: string;
  @Input('inputId') inputId: string;

  dateControl = new UntypedFormControl('');
  timeControl = new UntypedFormControl('');
  secretFormControl: UntypedFormGroup = new UntypedFormGroup({
    date: this.dateControl,
    time: this.timeControl,
  });

  private subscriptions: Subscription = new Subscription();

  constructor(private readonly dateTimeService: DatetimeService) {}

  ngOnInit(): void {
    const controlValue$ = this.formControlInput.valueChanges.pipe(
      startWith(this.formControlInput.value),
      distinctUntilChanged()
    );
    const tz$ = this.dateTimeService.timeZone$;
    const obs1$ = combineLatest([controlValue$, tz$]).pipe(
      map(([value, tz]) => ({ value, tz })),
      map(({ value, tz }) => {
        const normalized = this.dateTimeService.normalizeToUtcIfNoOffset(
          value ?? ''
        );
        const date = new Date(normalized);
        if (!value || Number.isNaN(date.valueOf())) {
          return null;
        }
        return {
          date: formatDate(date, 'yyyy-MM-dd', 'en-us', tz),
          time: formatDate(date, 'HH:mm:ss', 'en-us', tz),
        };
      }),
      filter(
        (values): values is { date: string; time: string } => values !== null
      ),
      tap((values) =>
        this.secretFormControl.setValue(values, { emitEvent: false })
      )
    );
    const obs2$ = combineLatest([
      this.secretFormControl.valueChanges,
      tz$,
    ]).pipe(
      filter(([values]) => Boolean(values?.date && values?.time)),
      map(([values, tz]) => ({ ...values, tz })),
      map(({ date, time, tz }) => {
        const localStr = `${date} ${time}`;
        const parsed = dayjs.tz(localStr, 'YYYY-MM-DD HH:mm:ss', tz);
        return parsed.isValid() ? parsed.toISOString() : null;
      }),
      filter((iso): iso is string => iso !== null),
      distinctUntilChanged(),
      tap((timestamp) => this.formControlInput.setValue(timestamp))
    );
    this.subscriptions.add(obs1$.subscribe());
    this.subscriptions.add(obs2$.subscribe());
    setTimeout(() => this.formControlInput.updateValueAndValidity());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
